import filter from 'lodash/filter'
import cloneDeep from 'lodash/cloneDeep'
import max from 'lodash/max'
import min from 'lodash/min'
import includes from 'lodash/includes'

import chroma from'chroma-js'

import React from 'react'
import DomainIcon from '@material-ui/icons/Domain'
import HotelIcon from '@material-ui/icons/Hotel'
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid'
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet'
import ThumbsUpDown from '@material-ui/icons/ThumbsUpDown'
import RateReviewIcon from '@material-ui/icons/RateReview'
import StarRateIcon from '@material-ui/icons/StarRate'
import FlightLandIcon from '@material-ui/icons/FlightLand'
import TrendingUpIcon from '@material-ui/icons/TrendingUp'
import LocalOfferIcon from '@material-ui/icons/LocalOffer'

import { withStyles } from '@material-ui/core/styles'

import istatLogo from './../assets/images/istat-logo.png'
import travelAppealLogo from './../assets/images/travel-appeal-logo.png'
import airbnbLogo from './../assets/images/airbnb-logo.png'

/* 
* Take a data set coming from Api and filter it with Region, DataType and Source from
* comparator component filter
*/
export const comparatorDataFilter = (dataSet, region, dataType, source) => {

    let data = cloneDeep(dataSet)

    /* Build filter map to select elements from data */
    const filterMap = {}
    if(!!region && region !== 'all') filterMap['region'] = region
    if(!!dataType && dataType !== 'all') filterMap['data_type'] = dataType
    
    data = filter(data, filterMap)

    /* If source is specified then filter data field */
    if(!!source && source !== 'all') {
        data.forEach(element => {
            element.data = filter(element.data, { key: source }) 
        })
    }
    
    return data
}

/*
* Color palette mapping
*/
export const ColorPalette = {
    positive: '#37e155',
    negative: '#ff0f2d',
    ta: '#141923',
    istat: '#0f96ff',
    airbnb: '#ff5a5f',
    taLight: '#43474f',
    taVeryLight: '#72757b',
    white: '#fafafa'
}

/*
* Return the correct color for populating evey heatmap given:
* starting color, value and range
*/
export const heatmapColor = (value, startRange, endRange, color) => {
    if(!startRange || ! endRange) return null

    const maxValue =  max([endRange, startRange])
    const minValue = min([endRange, startRange])
    const stepSize = Math.floor((maxValue - minValue) / 4 * 100) / 100

    let colorBrightnessLevel = 0
    while (maxValue - (stepSize * colorBrightnessLevel) > value) {
        colorBrightnessLevel++
    }

    return chroma(color).brighten(colorBrightnessLevel / 2)
}

/*
* In DataVisualizer, data are visualized in 3 way based on region and data type:
* - summary (with ALL data type shown for both ALL region or ONE region)
* - table (to display ALL region data about ONE data type)
* - detail (to display only ONE data type for ONE region)
*/
export const dataVisualizerMode = (region, dataType, source) => {
    switch (true) {
        case (region == 'all' && dataType != 'all'):
            return 'table'
        case (region != 'all' && dataType != 'all'):
            return 'detail'
        default:
            return 'summary'
    }
}

export const detailRowMode = (region, dataType, source) => {
    switch (true) {
        case (includes(['accommodations_in_italy', 'total_accommodations'], dataType)):
            return 'compare'
        case (!!source):
            return 'source_table'
        default:
            return 'default'
    }
}

/*
* By defualt Data field inside any record has an order and by convention main key of default value is equal to record Data Type
*/
export const DataTypeDataKeyOrder = {
    'accommodations_in_italy': ['percent_accommodations_in_italy_vs_italy', 'accommodations_in_italy'],
    'total_accommodations': ['percent_total_accommodations_vs_italys', 'total_accommodations'],
    'online_accommodations': ['online_accommodations'],
    'online_apartments': ['online_apartments'],
    // 'percent_airbnb_accommodations': ['percent_airbnb_accommodations'],
    'economics_per_inhabitant': ['economics_per_inhabitant'],
    
    'accommodations_reviews': ['accommodations_reviews'],
    'apartments_reviews': ['apartments_reviews'],
    'percent_accommodation_satisfaction': ['percent_accommodation_satisfaction'],
    'percent_apartment_satisfaction': ['percent_apartment_satisfaction'],
    'percent_airbnb_5_stars_review': ['percent_airbnb_5_stars_review'],

    'total_arrivals': ['italy', 'united-states', 'united-kingdom', 'france', 'germany', 'total_arrivals'],
    'percent_variance_arrivals': ['italy', 'united-states', 'united-kingdom', 'france', 'germany', 'percent_variance_arrivals'],
    'total_reviews': ['italy', 'united-states', 'united-kingdom', 'france', 'germany', 'total_reviews'],
    'total_attendances_airbnb': ['italy', 'united-states', 'united-kingdom', 'france', 'germany', 'total_attendances_airbnb'],
    'percent_variance_arrivals_airbnb': ['italy', 'united-states', 'united-kingdom', 'france', 'germany', 'percent_variance_arrivals_airbnb']
}

export const DataTypeIcons = {
    'accommodations_in_italy': DomainIcon,
    'total_accommodations': HotelIcon,
    'online_accommodations': PhoneAndroidIcon,
    'online_apartments': LocalOfferIcon,
    // 'percent_airbnb_accommodations': HotelIcon,
    'economics_per_inhabitant': AccountBalanceWalletIcon,

    'accommodations_reviews': RateReviewIcon,
    'apartments_reviews': RateReviewIcon,
    'percent_accommodation_satisfaction': ThumbsUpDown,
    'percent_apartment_satisfaction': ThumbsUpDown,
    'percent_airbnb_5_stars_review': StarRateIcon,

    'total_arrivals': FlightLandIcon,
    'percent_variance_arrivals': TrendingUpIcon,
    'total_reviews': RateReviewIcon,
    'total_attendances_airbnb': HotelIcon,
    'percent_variance_arrivals_airbnb': TrendingUpIcon
}

export const getDataTypeIcon = (dataType, iconStyle) => {

    const styles = { icon: {...iconStyle}}
    
    function Icon(props) {
        const { classes } = props
        const IconComponent = DataTypeIcons[dataType]
        return <IconComponent className={classes.icon}/>
    }

    const StyledIcon = withStyles(styles)(Icon)
    return <StyledIcon/>
}

export const getSourceIcon = (source, iconStyle) => {

    const styles = { icon: {...iconStyle}}

    function Icon(props) {
        const { classes } = props
        switch (source) {
            case 'istat':
                return <img className={classes.icon} src={istatLogo} alt="istat"/>
            case 'airbnb':
                return <img className={classes.icon} src={airbnbLogo} alt="airbnb"/>
            case 'ta':
                return <img className={classes.icon} src={travelAppealLogo} alt="travelappeal"/>
            default:
                return <img className={classes.icon} src={travelAppealLogo} alt="travelappeal"/>
        }
    }

    const StyledIcon = withStyles(styles)(Icon)
    return <StyledIcon/>
}

/* Same code save!!! */
/*
    switch (true) {
        case value < (minValue + stepSize):
            
            break;
        case value < (minValue + stepSize * 2):
            
            break;
        case value < (minValue + stepSize * 3):
            
            break;
        case value < (minValue + stepSize * 4):
            
            break;
        case value < (minValue + stepSize * 5):
            
            break;
        default:
            break;
    }


    let sourceColor = colorPalette.ta
    switch (source) {
        case 'istat':
            sourceColor = colorPalette.istat
            break
        case 'ta':
            sourceColor = colorPalette.ta
            break
        case 'airbnb':
            sourceColor = colorPalette.airbnb
            break
    }

    const positiveColor = colorPalette.positive
    const negativeColor = colorPalette.negative
    */


// WEBPACK FOOTER //
// ./src/libs/DataUtilities.js